import React from 'react';
import classNames from 'classnames';

type Props = {
  /** Visible label per step, in order. Its length defines the number of steps. */
  stepLabels: string[];
  /** 1-based. */
  currentStep: number;
  /** Highest step reached so far; everything up to it can be jumped back to. */
  maxVisitedStep: number;
  onStepSelect: (step: number) => void;
  ariaLabel: string;
};

/**
 * Inline step indicator for the user wizard: dot and label side by side, solid
 * connector, already-visited steps are real buttons so you can jump back.
 *
 * Not built on src/components/ui/Stepper: that one stacks the label under the
 * dot, uses a dashed connector and is intentionally non-interactive. Sharing a
 * component would mean two disjoint render branches behind a `layout` prop —
 * a fork with extra steps — plus a change to the public registration funnel.
 */
const WizardStepBar: React.FC<Props> = ({ stepLabels, currentStep, maxVisitedStep, onStepSelect, ariaLabel }) => (
  <nav aria-label={ariaLabel} className="surface-base border-subtle-bottom flex-shrink-0 px-32 py-16">
    <ol className="flex items-center gap-12">
      {stepLabels.map((label, index) => {
        const step = index + 1;
        const isCurrent = step === currentStep;
        const isSelectable = step <= maxVisitedStep && !isCurrent;

        const dot = (
          <span
            aria-hidden
            className={classNames(
              // width/height are NOT on the project's 1rem = 10px spacing scale
              // (only padding/margin/gap/inset are), hence the explicit rem values.
              'flex h-[2.4rem] w-[2.4rem] flex-shrink-0 items-center justify-center rounded-full typo-text-12--bold',
              isCurrent ? 'surface-primary-gradient text-primary-button' : 'surface-active text-full',
            )}>
            {step}
          </span>
        );
        const text = (
          <span className={classNames('typo-text-14--bold', isCurrent ? 'text-full' : 'text-faded')}>{label}</span>
        );

        return (
          <React.Fragment key={label}>
            {/* Fixed connector length instead of flex-1: stretched across the
                whole overlay the two steps read as two unrelated headings
                sitting in opposite corners. Shrinks on narrow viewports. */}
            {index > 0 && <li aria-hidden className="border-subtle-top h-0 w-[6.4rem] min-w-[2.4rem]" />}
            <li aria-current={isCurrent ? 'step' : undefined} className="flex-shrink-0">
              {isSelectable ? (
                <button
                  type="button"
                  onClick={() => onStepSelect(step)}
                  className="flex items-center gap-8 rounded-sm px-4 py-2 hover:opacity-80">
                  {dot}
                  {text}
                </button>
              ) : (
                <span className="flex items-center gap-8 px-4 py-2">
                  {dot}
                  {text}
                </span>
              )}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

export default WizardStepBar;
